/**
 * Browse apps (slice 3b spec §5.6; Paper 45, 45b): the page that adds
 * things, from `GET /api/integrations/catalog`. Only apps with a catalog
 * file, by category; a category with none isn't shown, and nothing is
 * "coming soon". Connect (or Add account) opens the connect window; what's
 * connected is managed on Integrations. The MCP directory (§6.4): Checked
 * first, eight then Show all, each with Add (Add MCP server filled in from
 * the entry) or Added; Add by URL in its head. `?filter=mcp` (Browse servers)
 * opens on it alone.
 */
import { BackLink } from "@crew/ui/components/back-link";
import { Button } from "@crew/ui/components/button";
import { Input } from "@crew/ui/components/input";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Check, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { errorHint } from "../../app/api";
import { ConnectWindow, type WindowApp } from "./connect-window";
import { useCatalog, type Catalog, type CatalogApp, type DirectoryEntry } from "./integrations-api";
import { AppIcon, StateLabel } from "./integrations-parts";
import {
  DIRECTORY_FIRST,
  MCP_CHIP,
  catalogChips,
  catalogSections,
  directoryAuth,
  directoryView,
  type Section,
} from "./integrations-view";
import { AddServerWindow } from "./mcp-windows";

/** On a phone a category shows three apps, then "Show N more" (spec §5.6). */
const PHONE_FIRST = 3;

/** Everyone connects their own (slice 6a §5.4), and can share it from Settings → Integrations. */
const BROWSE_FOOTNOTE =
  "Every app has the same three access levels: Read only, Read and send (or write), Full access. What you connect is yours until you share it.";

/** Add MCP server, by URL (no entry) or from an entry. */
type ServerAdd = { entry: DirectoryEntry | null } | null;

/** The chip in the URL (`?filter=`): All, a category, or MCP servers (Browse servers). */
function useCategory(): [string, (key: string) => void] {
  const [params, setParams] = useSearchParams();
  const set = (key: string) =>
    setParams(
      (p) => {
        if (key === "all") p.delete("filter");
        else p.set("filter", key);
        return p;
      },
      { replace: true },
    );
  return [params.get("filter") ?? "all", set];
}

export function BrowseApps() {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useCategory();
  const [win, setWin] = useState<WindowApp | null>(null);
  const [adding, setAdding] = useState<ServerAdd>(null);
  const data = catalog.data;
  const can = data?.available === true;

  return (
    <div className="flex flex-col gap-7 md:gap-6" data-browse-apps>
      <div className="flex flex-col gap-4 pt-1">
        <BrowseHead query={query} onQuery={setQuery} />
        {data ? (
          <Chips
            apps={data.apps}
            directory={data.directory}
            value={category}
            onChange={setCategory}
          />
        ) : null}
        {data && !data.available ? (
          <p className="text-sm text-muted-foreground">
            Apps aren't available right now. Your crew answers without them.
          </p>
        ) : null}
      </div>
      {data ? (
        <CatalogBody
          catalog={data}
          view={{ query, category }}
          can={can}
          onConnect={setWin}
          onAdd={(entry) => setAdding({ entry })}
        />
      ) : (
        <NotLoaded error={catalog.isError ? catalog.error : null} />
      )}
      <p className="text-meta text-muted-foreground">{BROWSE_FOOTNOTE}</p>
      <ConnectWindow
        app={win}
        mode={{ kind: "connect" }}
        onClose={() => setWin(null)}
        // Signing in in its own tab: wait on Integrations, where the row counts down.
        onSignInOpened={() => void navigate("/settings/integrations")}
      />
      <AddServerWindow
        open={adding !== null}
        entry={adding?.entry ?? null}
        onClose={() => setAdding(null)}
        onSignInOpened={() => void navigate("/settings/integrations")}
        // Added: back to Integrations, where its tools open for review (spec §6.2).
        onAdded={(server) =>
          navigate(`/settings/integrations?connected=${encodeURIComponent(`mcp:${server.name}`)}`)
        }
      />
    </div>
  );
}

/** The apps by category, then the MCP directory: under MCP servers, the directory alone. */
function CatalogBody({
  catalog,
  view,
  can,
  onConnect,
  onAdd,
}: {
  catalog: Catalog;
  view: { query: string; category: string };
  /** Anyone, with apps available: Connect, Add and Add by URL, for themself (slice 6a §5.4). */
  can: boolean;
  onConnect: (app: CatalogApp) => void;
  onAdd: (entry: DirectoryEntry | null) => void;
}) {
  const servers = view.category === MCP_CHIP;
  const directory = catalog.directory.length > 0 && (servers || view.category === "all");
  return (
    <>
      {servers ? null : (
        <Sections
          sections={catalogSections(catalog.apps, view)}
          query={view.query}
          canConnect={can}
          onConnect={onConnect}
          onAddByUrl={can ? () => onAdd(null) : undefined}
        />
      )}
      {directory ? (
        <Directory
          entries={directoryView(catalog.directory, view.query)}
          total={catalog.directory.length}
          all={servers}
          query={view.query}
          canAdd={can}
          onAdd={onAdd}
        />
      ) : null}
    </>
  );
}

/** A spinner while the catalog loads, or why it didn't. */
function NotLoaded({ error }: { error: Error | null }) {
  if (error) return <p className="text-body text-destructive">{errorHint(error)}</p>;
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
}

/** The back link, the title and its line, and the search box (Paper 45). */
function BrowseHead({ query, onQuery }: { query: string; onQuery: (q: string) => void }) {
  return (
    <>
      <BackLink href="/settings/integrations">Integrations</BackLink>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-[24px] leading-[30px] font-semibold tracking-tight text-foreground">
            Browse apps
          </h2>
          <p className="text-sm text-muted-foreground">
            Add an app or a server. Anything connected is managed on Integrations.
          </p>
        </div>
        <label className="relative block md:w-80">
          <span className="sr-only">Search apps</span>
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-hint" />
          <Input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search apps"
            className="h-10.5 bg-card pl-9 md:h-9.5"
          />
        </label>
      </div>
    </>
  );
}

/** All, then each category with an app; on a phone they scroll sideways (spec §5.6). */
function Chips({
  apps,
  directory,
  value,
  onChange,
}: {
  apps: CatalogApp[];
  directory: DirectoryEntry[];
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Categories"
      className="-mx-4 flex gap-2 overflow-x-auto px-4 py-px md:mx-0 md:flex-wrap md:overflow-visible md:px-0"
    >
      {catalogChips(apps, directory).map((c) => (
        <button
          key={c.key}
          type="button"
          aria-pressed={value === c.key}
          onClick={() => onChange(c.key)}
          className={cn(
            "flex h-9 shrink-0 items-center rounded-full px-3 text-meta font-medium whitespace-nowrap md:h-7.5",
            value === c.key
              ? "bg-foreground text-background"
              : "text-foreground shadow-[0_0_0_1px_rgb(31_42_68/0.14)]",
          )}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

function Sections({
  sections,
  query,
  canConnect,
  onConnect,
  onAddByUrl,
}: {
  sections: Section[];
  query: string;
  canConnect: boolean;
  onConnect: (app: CatalogApp) => void;
  /** Empty search: "No apps match" and Add by URL (spec §5.6). */
  onAddByUrl?: () => void;
}) {
  if (sections.length === 0) {
    return (
      <div className="flex flex-wrap items-center gap-3" data-no-match>
        <p className="text-sm text-muted-foreground">
          {query.trim() ? `No apps match '${query.trim()}'.` : "No apps here yet."}
        </p>
        {query.trim() && onAddByUrl ? (
          <Button variant="outline" size="sm" className="bg-card" onClick={onAddByUrl}>
            <Plus className="size-3" /> Add by URL
          </Button>
        ) : null}
      </div>
    );
  }
  return sections.map((s) => (
    <CategorySection key={s.category} section={s} canConnect={canConnect} onConnect={onConnect} />
  ));
}

function CategorySection({
  section,
  canConnect,
  onConnect,
}: {
  section: Section;
  canConnect: boolean;
  onConnect: (app: CatalogApp) => void;
}) {
  const [more, setMore] = useState(false);
  const shown = more ? section.apps : section.apps.slice(0, PHONE_FIRST);
  const hidden = section.apps.slice(PHONE_FIRST);
  return (
    <section
      aria-label={section.name}
      data-category={section.category}
      className="flex flex-col gap-2.5 pt-1 md:gap-3"
    >
      <div className="flex items-baseline gap-2">
        <h3 className="text-base leading-[22px] font-semibold text-foreground">{section.name}</h3>
        <span className="hidden text-meta text-hint md:inline">{section.apps.length}</span>
      </div>
      {/* Desktop: a card each, four across (Paper 45). */}
      <div className="hidden grid-cols-3 gap-4 md:grid xl:grid-cols-4">
        {section.apps.map((app) => (
          <AppTile key={app.app} app={app} canConnect={canConnect} onConnect={onConnect} />
        ))}
      </div>
      {/* Phone: rows in one card, three then "Show N more" (Paper 45b). */}
      <div className="flex flex-col rounded-panel bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)] md:hidden">
        {shown.map((app, i) => (
          <AppRow
            key={app.app}
            app={app}
            canConnect={canConnect}
            onConnect={onConnect}
            last={i === shown.length - 1 && (more || hidden.length === 0)}
          />
        ))}
        {!more && hidden.length > 0 ? (
          <button
            type="button"
            onClick={() => setMore(true)}
            className="flex items-center gap-1.5 px-3.5 py-3 text-left text-meta"
          >
            <span className="shrink-0 font-medium text-foreground underline underline-offset-2">
              Show {hidden.length} more
            </span>
            <span className="truncate text-muted-foreground">
              {hidden.map((a) => a.name).join(", ")}
            </span>
          </button>
        ) : null}
      </div>
    </section>
  );
}

/** Connect, or Connected and Add account (the same window); none for a member or when unavailable. */
function TileButton({
  app,
  canConnect,
  onConnect,
  className,
}: {
  app: CatalogApp;
  canConnect: boolean;
  onConnect: (app: CatalogApp) => void;
  className?: string;
}) {
  // At the limit there's nothing to add; the tile still says Connected (Devin
  // and Codex on #247: the server's count, a connect in progress included).
  if (!canConnect || !app.can_add) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("h-7 shrink-0 bg-card px-2.5 text-meta", className)}
      onClick={() => onConnect(app)}
      aria-label={`${app.accounts > 0 ? "Add account" : "Connect"}: ${app.name}`}
    >
      {app.accounts > 0 ? "Add account" : "Connect"}
    </Button>
  );
}

function AppTile({
  app,
  canConnect,
  onConnect,
}: {
  app: CatalogApp;
  canConnect: boolean;
  onConnect: (app: CatalogApp) => void;
}) {
  return (
    <div
      data-catalog-app={app.app}
      className="flex flex-col gap-2.5 rounded-panel bg-card px-4 py-3.5 shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
    >
      <div className="flex items-center gap-2.5">
        <AppIcon app={app.app} icon={app.icon} mark={app.mark} />
        <span className="text-sm font-semibold text-foreground">{app.name}</span>
      </div>
      <p className="line-clamp-2 min-h-9.5 text-meta leading-[19px] text-muted-foreground">
        {app.blurb}.
      </p>
      <div
        className={cn("flex items-center", app.accounts > 0 ? "justify-between" : "justify-end")}
      >
        {app.accounts > 0 ? (
          <StateLabel tone="moss" className="text-xs">
            Connected
          </StateLabel>
        ) : null}
        <TileButton app={app} canConnect={canConnect} onConnect={onConnect} />
      </div>
    </div>
  );
}

function AppRow({
  app,
  canConnect,
  onConnect,
  last,
}: {
  app: CatalogApp;
  canConnect: boolean;
  onConnect: (app: CatalogApp) => void;
  last: boolean;
}) {
  return (
    <div
      data-catalog-app={app.app}
      className={cn("flex items-center gap-3 px-3.5 py-3", !last && "border-b border-border")}
    >
      <AppIcon app={app.app} icon={app.icon} mark={app.mark} />
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="text-sm font-semibold text-foreground">{app.name}</span>
        <span className="text-meta leading-[18px] text-muted-foreground">
          {app.accounts > 0 ? <span className="font-medium text-moss">Connected · </span> : null}
          {app.blurb}.
        </span>
      </div>
      <TileButton
        app={app}
        canConnect={canConnect}
        onConnect={onConnect}
        className="h-8 w-auto min-w-19.5 justify-center"
      />
    </div>
  );
}

/** Add, or Added for an entry the workspace already has (spec §6.4); none for a member. */
function AddButton({
  entry,
  canAdd,
  onAdd,
}: {
  entry: DirectoryEntry;
  canAdd: boolean;
  onAdd: (e: DirectoryEntry) => void;
}) {
  if (entry.added) {
    return (
      <span className="flex h-8 w-16 shrink-0 items-center justify-center gap-1 text-xs text-moss">
        <Check className="size-3" /> Added
      </span>
    );
  }
  if (!canAdd) return <span className="hidden w-16 shrink-0 md:block" />;
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 w-16 shrink-0 bg-card text-meta"
      onClick={() => onAdd(entry)}
      aria-label={`Add ${entry.name}`}
    >
      Add
    </Button>
  );
}

/** Checked with Crew, or not yet (spec §6.4). */
function CheckedMark({ checked }: { checked: boolean }) {
  return checked ? (
    <span className="flex shrink-0 items-center gap-1 text-xs text-moss">
      <Check className="size-3" /> Checked
    </span>
  ) : (
    <span className="shrink-0 text-xs text-hint">Not checked yet</span>
  );
}

function DirectoryRow({
  entry: e,
  canAdd,
  onAdd,
}: {
  entry: DirectoryEntry;
  canAdd: boolean;
  onAdd: (e: DirectoryEntry) => void;
}) {
  return (
    <div
      data-directory={e.id}
      className="flex items-center gap-3 border-b border-border px-3.5 py-3 md:px-4"
    >
      <AppIcon app="directory" logo={e.logo} mark={e.name.slice(0, 2)} />
      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="truncate text-sm font-semibold text-foreground">{e.name}</span>
        <span className="hidden truncate text-meta text-muted-foreground md:block">{e.blurb}</span>
        <span className="flex items-center gap-1.5 text-meta text-muted-foreground md:hidden">
          {directoryAuth(e)}
          <span className="text-hint">·</span>
          <CheckedMark checked={e.checked} />
        </span>
      </div>
      <span className="hidden w-56 shrink-0 truncate font-mono text-xs text-muted-foreground lg:inline">
        {e.url.replace(/^https?:\/\//, "")}
      </span>
      <span className="hidden w-20 shrink-0 md:flex">
        <span className="rounded-tag px-1.5 text-xs leading-[18px] text-muted-foreground shadow-[inset_0_0_0_1px_rgb(31_42_68/0.14)]">
          {directoryAuth(e)}
        </span>
      </span>
      <span className="hidden w-28 shrink-0 md:flex">
        <CheckedMark checked={e.checked} />
      </span>
      <AddButton entry={e} canAdd={canAdd} onAdd={onAdd} />
    </div>
  );
}

/**
 * The MCP directory (spec §5.6, §6.4; Paper 45, 45b): Checked first, eight
 * then Show all (all of it under Browse servers), Add by URL in its head, and
 * the credit to integrations.sh.
 */
function Directory({
  entries,
  total,
  all,
  query,
  canAdd,
  onAdd,
}: {
  entries: DirectoryEntry[];
  total: number;
  all: boolean;
  query: string;
  canAdd: boolean;
  onAdd: (e: DirectoryEntry | null) => void;
}) {
  const [more, setMore] = useState(false);
  const shown = all || more ? entries : entries.slice(0, DIRECTORY_FIRST);
  return (
    <section aria-label="MCP servers" data-directory-section className="flex flex-col gap-3 pt-2">
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2">
            <h3 className="text-base leading-[22px] font-semibold text-foreground">MCP servers</h3>
            <span className="hidden text-meta text-hint md:inline">{total}</span>
          </div>
          <p className="text-meta text-muted-foreground">
            Popular servers from the integrations.sh directory. You choose which of their tools are
            on; writes start off.
          </p>
        </div>
        {canAdd ? (
          <Button
            variant="outline"
            className="hidden bg-card md:inline-flex"
            onClick={() => onAdd(null)}
          >
            <Plus className="size-3" /> Add by URL
          </Button>
        ) : null}
      </div>
      <div className="flex flex-col overflow-hidden rounded-panel bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
        {shown.length === 0 ? (
          <p className="border-b border-border px-4 py-3 text-sm text-muted-foreground">
            No servers match '{query.trim()}'.
          </p>
        ) : null}
        {shown.map((e) => (
          <DirectoryRow key={e.id} entry={e} canAdd={canAdd} onAdd={onAdd} />
        ))}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-3 md:px-4">
          <p className="flex-1 text-xs text-muted-foreground">
            Listed by{" "}
            <a
              href="https://integrations.sh"
              target="_blank"
              rel="noreferrer"
              className="py-2 underline"
            >
              integrations.sh
            </a>
            , from Claude's connector directory and its own picks. Checked means Crew has connected
            it.
          </p>
          {!all && !more && entries.length > shown.length ? (
            <button
              type="button"
              onClick={() => setMore(true)}
              className="-my-2 py-2 text-meta font-medium text-foreground underline underline-offset-2"
            >
              Show all {entries.length}
            </button>
          ) : null}
          {canAdd ? (
            <button
              type="button"
              onClick={() => onAdd(null)}
              className="-my-2 flex items-center gap-1 py-2 text-meta font-medium text-foreground md:hidden"
            >
              <Plus className="size-3" /> Add by URL
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
